import { SpecFieldDropdown, SpecFieldInfo, SpecificationParameter, XCelumUiParametersMetadata } from '@celum/formats/domain';

/**
 * Pure, dependency-free translation of a `SpecificationParameter` (as parsed out of a specification contract) into
 * the `SpecFieldInfo` shape `spec-params.component` renders. Extracted out of `FormatFormService` so it can be used
 * directly by `spec-params.component` (in a different lib) without coupling that component to `FormatFormService`/
 * `Format`, and reused as-is by `BaseFormatFormService`.
 */
export function collectSpecFieldInfo(specParameters: SpecificationParameter): SpecFieldInfo {
  const metadata = specParameters['x-celum-ui-parameters-metadata'];
  const dropdown = parseDropdown(specParameters, metadata);

  return {
    inputType: getInputTypeOfField(specParameters),
    name: metadata?.name?.default || specParameters.key,
    unit: metadata?.unit?.default || '',
    placeholder: specParameters?.default ? (specParameters.default as string) : '',
    example: specParameters?.examples?.[0] ?? specParameters?.example ?? '',
    description: specParameters?.description || '',
    group: metadata?.group?.default,
    excludeFromUI: metadata?.excludeFromUI?.default || false,
    dropdown,
    dependsOn: metadata?.dependsOn
  };
}

export function getInputTypeOfField(specParameters: SpecificationParameter): string {
  if (specParameters?.enum) {
    return 'select';
  }
  if (specParameters?.type === 'boolean') {
    return 'checkbox';
  }
  if (specParameters?.type === 'number') {
    return 'number';
  }

  return 'text';
}

export function parseDropdown(specParameters: SpecificationParameter, metadata: XCelumUiParametersMetadata): SpecFieldDropdown | null {
  let dropdown: SpecFieldDropdown | null = null;
  const values = specParameters.enum ?? specParameters.items?.enum;
  if (metadata?.dropdown?.selectionType?.default === 'multiple') {
    dropdown = {
      selectionType: metadata.dropdown.selectionType?.default,
      values,
      defaultValues: specParameters.default ? (specParameters.default as string[]).filter(value => values?.includes(value)) : []
    };
  } else if (values) {
    // backwards compatibility if dropdown metadata is not provided, we assume it's single select if enum is present
    dropdown = {
      selectionType: 'single',
      values,
      showEmptyOption: !!metadata?.dropdown?.showEmptyOption?.default
    };
  }
  return dropdown;
}
